(function () {
  'use strict';

  const LOCATION_STORAGE_KEY = 'peachmode_rush_location';
  const RETURN_STATE_KEY = 'peachmode_rush_return';
  const RETURN_STATE_TTL = 30 * 60 * 1000;
  const LOCATION_TTL = 60 * 60 * 1000;
  const ATTRIBUTE_KEYS = [
    'rush_order',
    'rush_same_day',
    'rush_service_level',
    'rush_variant_ids',
    'rush_user_lat',
    'rush_user_long'
  ];
  const LINE_PROPERTY_KEYS = {
    rush_order: '_rush_order',
    rush_same_day: '_rush_same_day',
    rush_service_level: '_rush_service_level'
  };

  function isTrue(value) {
    return String(value).toLowerCase() === 'true';
  }

  function coordinateValue(value) {
    const coordinate = Number(value);
    return Number.isFinite(coordinate) ? coordinate.toFixed(6) : '';
  }

  function readRushLocation() {
    try {
      const location = JSON.parse(localStorage.getItem(LOCATION_STORAGE_KEY));
      const isFresh = location && Number.isFinite(location.savedAt) && Date.now() - location.savedAt < LOCATION_TTL;
      const hasCoordinates = Number.isFinite(location?.latitude) && Number.isFinite(location?.longitude);
      return isFresh && hasCoordinates ? location : null;
    } catch (error) {
      return null;
    }
  }

  function cartAttributesFor(cart) {
    const rushItems = cart.items.filter((item) => isTrue(item.properties?.[LINE_PROPERTY_KEYS.rush_order]));
    const location = rushItems.length ? readRushLocation() : null;
    const rushVariantIds = [...new Set(rushItems.map((item) => String(item.variant_id)))];
    const levels = [...new Set(rushItems.map((item) => item.properties?.[LINE_PROPERTY_KEYS.rush_service_level]).filter(Boolean))];
    const containsStandardItems = rushItems.length !== cart.items.length;
    let serviceLevel = 'STANDARD';

    if (rushItems.length) {
      serviceLevel = levels.length === 1 && !containsStandardItems ? levels[0] : 'MIXED';
    }

    return {
      rush_order: rushItems.length ? 'true' : 'false',
      rush_same_day: rushItems.length && !containsStandardItems && levels.length === 1 && levels[0] === 'SAME_DAY' ? 'true' : 'false',
      rush_service_level: serviceLevel,
      rush_variant_ids: rushVariantIds.join(','),
      rush_user_lat: location ? coordinateValue(location.latitude) : '',
      rush_user_long: location ? coordinateValue(location.longitude) : ''
    };
  }

  function attributesChanged(current, next) {
    return ATTRIBUTE_KEYS.some((key) => String(current?.[key] || '') !== next[key]);
  }

  let cartSyncPromise = Promise.resolve();

  function queueCartAttributeSync(cart) {
    cartSyncPromise = cartSyncPromise.then(async () => {
      const currentCart = cart || await fetch(`${window.themeVariables.routes.cartUrl}.js`, {
        credentials: 'same-origin',
        cache: 'no-store'
      }).then((response) => response.json());
      const attributes = cartAttributesFor(currentCart);

      if (!attributesChanged(currentCart.attributes, attributes)) return;

      await fetch(`${window.themeVariables.routes.cartUrl}/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes })
      });
    }).catch(() => {
      // Cart checkout remains usable if metadata synchronization fails.
    });

    return cartSyncPromise;
  }

  class RushProductAvailability extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = document.getElementById(this.dataset.formId);
      this.banner = this.querySelector('[data-rush-availability-banner]');
      this.titleElement = this.querySelector('[data-rush-availability-title]');
      this.message = this.querySelector('[data-rush-availability-message]');
      this.tag = this.querySelector('[data-rush-availability-tag]');
      this.returnLink = this.querySelector('[data-rush-return-link]');
      this.checkoutFields = Array.from(this.querySelectorAll('[data-rush-checkout-attribute]'));
      this.lineProperties = Array.from(this.querySelectorAll('[data-rush-line-property]'));
      this.productData = JSON.parse(this.querySelector('[data-rush-product-data]').textContent);
      this.variantDataById = new Map(this.productData.variants.map((variant) => [String(variant.id), variant]));
      this.availabilityByVariant = new Map();
      this.selectedVariantId = String(this.form?.querySelector('[name="id"]')?.value || this.productData.selectedVariantId);

      this.setupReturnLink();
      this.resetSelectedVariant();
      this.form?.addEventListener('variant:changed', this.onVariantChanged.bind(this));
      this.fetchAvailability();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    onVariantChanged(event) {
      this.selectedVariantId = String(event.detail?.variant?.id || '');
      this.updateEligibleSizeBadges();
      this.renderSelectedVariant();
    }

    async fetchAvailability() {
      const location = readRushLocation();
      if (!location) return;

      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;

      try {
        const response = await fetch(this.dataset.endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            latitude: location.latitude,
            longitude: location.longitude,
            accuracyMeters: location.accuracyMeters,
            shopifyProductId: String(this.productData.shopifyProductId),
            shopifyVariantIds: this.productData.variants.map((variant) => String(variant.id))
          }),
          signal: controller.signal
        });

        if (!response.ok) throw new Error(`Rush availability request failed with ${response.status}`);
        const data = await response.json();

        if (!data.locationServiceable || !Array.isArray(data.variants)) {
          this.resetSelectedVariant();
          return;
        }

        data.variants.forEach((variant) => {
          this.availabilityByVariant.set(String(variant.shopifyVariantId), variant);
        });
        this.updateEligibleSizeBadges();
        this.renderSelectedVariant();
      } catch (error) {
        if (error.name !== 'AbortError') this.resetSelectedVariant();
      }
    }

    renderSelectedVariant() {
      const availability = this.availabilityByVariant.get(this.selectedVariantId);
      const deliveryTags = Array.isArray(availability?.deliveryTags) ? availability.deliveryTags : [];
      const rushAvailable = availability?.rushAvailable === true && deliveryTags.includes('RUSH');

      if (!rushAvailable) {
        this.resetSelectedVariant();
        this.renderAvailabilitySummary();
        return;
      }

      const sameDay = deliveryTags.includes('SAME_DAY');
      const serviceLevel = sameDay ? 'SAME_DAY' : 'NEXT_DAY';
      const location = readRushLocation();
      this.setCheckoutValues({
        rush_order: 'true',
        rush_same_day: sameDay ? 'true' : 'false',
        rush_service_level: serviceLevel,
        rush_variant_ids: this.selectedVariantId,
        rush_user_lat: coordinateValue(location?.latitude),
        rush_user_long: coordinateValue(location?.longitude)
      });
      this.setLineProperties({
        rush_order: 'true',
        rush_same_day: sameDay ? 'true' : 'false',
        rush_service_level: serviceLevel
      }, true);

      this.titleElement.textContent = 'Rush delivery available';
      this.message.textContent = sameDay
        ? 'This option is eligible for same-day delivery. Online payment required.'
        : 'This option is eligible for next-day delivery. Online payment required.';
      this.tag.textContent = sameDay ? 'Same day' : 'Next day';
      this.banner.hidden = false;
    }

    renderAvailabilitySummary() {
      const eligibleSizes = this.getEligibleSizeValues();
      const hasRushVariant = Array.from(this.availabilityByVariant.values()).some((availability) => {
        const tags = Array.isArray(availability.deliveryTags) ? availability.deliveryTags : [];
        return availability.rushAvailable === true && tags.includes('RUSH');
      });

      if (!hasRushVariant) return;

      this.titleElement.textContent = 'Rush delivery available';
      this.message.textContent = eligibleSizes.length
        ? `Available for sizes ${this.formatList(eligibleSizes)}. Select a highlighted size.`
        : 'Select a highlighted option for faster delivery.';
      this.tag.textContent = eligibleSizes.length
        ? (eligibleSizes.length === 1 ? '1 size' : `${eligibleSizes.length} sizes`)
        : 'Rush';
      this.banner.hidden = false;
    }

    getEligibleSizeValues() {
      const sizeIndex = Number(this.productData.sizeOptionPosition) - 1;
      if (sizeIndex < 0) return [];

      const selectedVariant = this.variantDataById.get(this.selectedVariantId);
      const sizes = [];

      this.availabilityByVariant.forEach((availability, variantId) => {
        const tags = Array.isArray(availability.deliveryTags) ? availability.deliveryTags : [];
        if (availability.rushAvailable !== true || !tags.includes('RUSH')) return;

        const variant = this.variantDataById.get(variantId);
        if (!variant?.options?.[sizeIndex]) return;

        const matchesOtherOptions = !selectedVariant || variant.options.every((value, index) => {
          return index === sizeIndex || value === selectedVariant.options[index];
        });

        if (matchesOtherOptions && !sizes.includes(variant.options[sizeIndex])) {
          sizes.push(variant.options[sizeIndex]);
        }
      });

      return sizes;
    }

    updateEligibleSizeBadges() {
      const sizePosition = Number(this.productData.sizeOptionPosition);
      if (!sizePosition) return;

      const eligibleSizes = new Set(this.getEligibleSizeValues());
      const selector = document.querySelector(`product-variants[form-id="${CSS.escape(this.dataset.formId)}"]`);
      if (!selector) return;

      selector.querySelectorAll(`[name="option${sizePosition}"]`).forEach((input) => {
        const swatch = input.closest('.block-swatch');
        if (!swatch) return;

        const isEligible = eligibleSizes.has(input.value);
        swatch.classList.toggle('rush-size-available', isEligible);

        const label = swatch.querySelector('.block-swatch__item');
        if (label) {
          if (isEligible) {
            label.setAttribute('title', 'Rush delivery available');
          } else {
            label.removeAttribute('title');
          }
        }
      });
    }

    formatList(values) {
      if (values.length < 2) return values[0] || '';
      if (values.length === 2) return `${values[0]} and ${values[1]}`;
      return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
    }

    setupReturnLink() {
      if (!this.returnLink) return;

      try {
        const state = JSON.parse(sessionStorage.getItem(RETURN_STATE_KEY));
        const isFresh = state && Date.now() - Number(state.savedAt) <= RETURN_STATE_TTL;
        const matchesProduct = state?.productPath === window.location.pathname;
        const referrer = document.referrer ? new URL(document.referrer) : null;
        const cameFromRush = referrer?.origin === window.location.origin
          && referrer.pathname === '/pages/peachmode-rush';

        if (isFresh && matchesProduct && cameFromRush && state.url) {
          this.returnLink.href = state.url;
        }
      } catch (error) {
        // The default Rush page remains available if return state cannot be read.
      }
    }

    resetSelectedVariant() {
      this.setCheckoutValues({
        rush_order: 'false',
        rush_same_day: 'false',
        rush_service_level: 'STANDARD',
        rush_variant_ids: '',
        rush_user_lat: '',
        rush_user_long: ''
      });
      this.setLineProperties({
        rush_order: 'true',
        rush_same_day: 'false',
        rush_service_level: 'STANDARD'
      }, false);
      this.banner.hidden = true;
    }

    setCheckoutValues(values) {
      this.checkoutFields.forEach((field) => {
        field.value = values[field.dataset.rushCheckoutAttribute];
      });

      document.documentElement.dispatchEvent(new CustomEvent('rush:product-availability-changed', {
        detail: {
          rushAvailable: isTrue(values.rush_order),
          variantId: this.selectedVariantId
        }
      }));
    }

    setLineProperties(values, enabled) {
      this.lineProperties.forEach((field) => {
        field.value = values[field.dataset.rushLineProperty];
        field.disabled = !enabled;
      });
    }
  }

  if (!customElements.get('rush-product-availability')) {
    customElements.define('rush-product-availability', RushProductAvailability);
  }

  document.documentElement.addEventListener('cart:updated', (event) => {
    queueCartAttributeSync(event.detail?.cart);
  });
})();
